const TAG = 'custom-size-editor';
const ADD_TO_CART_SELECTOR =
  'button[name="add"], input[name="add"], .add-to-cart-button';

const escapeCss = (value) =>
  window.CSS?.escape?.(String(value)) || String(value).replace(/["\\]/g, '\\$&');
const cleanMeasurements = (values) =>
  (Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean);

class CustomSizeEditor extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    const config = this.parseConfig();
    if (!config) return;

    this.initialized = true;
    this.config = config;
    this.sectionRoot = this.closest('.shopify-section') || document;
    this.productFormId = this.dataset.productFormId || '';

    this.variantId = '';
    this.measurements = [];
    this.isCustom = false;
    this.editorOpen = false;
    this.transitionPending = false;
    this.draft = new Map();
    this.confirmed = new Map();

    this.refs = {
      editor: this.querySelector('[data-custom-size-measurements]'),
      grid: this.querySelector('[data-custom-size-measurement-grid]'),
      validation: this.querySelector('[data-custom-size-validation-message]'),
      properties: this.querySelector('[data-custom-size-properties]'),
      template: this.querySelector('[data-custom-size-field-template]'),
      confirm: this.querySelector('[data-custom-size-action="confirm"]'),
    };

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onAddToCartClick = this.onAddToCartClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.sectionRoot.addEventListener('click', this.onAddToCartClick, true);
    this.sectionRoot.addEventListener('submit', this.onSubmit, true);

    this.dataset.customSizeReady = '';
    this.renderView();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onInput);
    this.sectionRoot?.removeEventListener('click', this.onAddToCartClick, true);
    this.sectionRoot?.removeEventListener('submit', this.onSubmit, true);

    this.restoreAddToCartState();
    this.setForm(null);
    this.clearProperties();
  }

  parseConfig() {
    try {
      return JSON.parse(
        this.querySelector('[data-custom-size-config]')?.textContent || '{}'
      );
    } catch (error) {
      console.error('Custom size configuration is invalid.', error);
      return null;
    }
  }

  setContext({ variantId = '', isCustom = false, form = null } = {}) {
    const wasCustom = this.isCustom;
    const previousSignature = JSON.stringify(this.measurements);

    this.setForm(form);
    this.variantId = String(variantId || '');
    this.isCustom = Boolean(isCustom);
    this.measurements = this.resolveMeasurements(this.variantId);
    this.transitionPending = false;

    if (!this.isCustom) {
      this.editorOpen = false;
      this.setEditorVisible(false);
      this.setValidation('');
      this.clearInputValidation();
      this.clearProperties();
      this.restoreAddToCartState();
      this.renderView();
      return;
    }

    const nextSignature = JSON.stringify(this.measurements);
    const measurementsChanged = previousSignature !== nextSignature;

    this.renderFields();

    if (!wasCustom || measurementsChanged) {
      this.editorOpen = !this.hasConfirmed();
    } else if (!this.hasConfirmed()) {
      this.editorOpen = true;
    }

    this.setEditorVisible(this.editorOpen);

    if (!this.measurements.length) {
      this.editorOpen = true;
      this.setEditorVisible(true);
      this.setValidation(this.dataset.messageMissing);
    } else {
      this.setValidation('');
    }

    this.renderView();
  }

  beginCustomTransition() {
    this.isCustom = true;
    this.transitionPending = true;
    this.updateAddToCartState();
    this.dispatchState();
  }

  refreshForm(form = null) {
    this.setForm(form);
    this.syncProperties();
    this.updateAddToCartState();
  }

  resolveMeasurements(variantId) {
    const variantMeasurements = this.config.variants?.[String(variantId)]
      ?.measurements;

    return cleanMeasurements(
      variantMeasurements?.length
        ? variantMeasurements
        : this.config.productMeasurements
    );
  }

  onClick(event) {
    const button = event.target.closest('[data-custom-size-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.customSizeAction;

    if (action === 'confirm') this.confirmMeasurements();
    if (action === 'cancel') this.cancelEditing();
  }

  onInput(event) {
    const input = event.target;

    if (
      !(input instanceof HTMLInputElement) ||
      !input.matches('[data-custom-size-measurement]')
    ) {
      return;
    }

    input.setCustomValidity('');
    input.removeAttribute('aria-invalid');
    this.draft.set(input.dataset.measurementName, input.value);
    this.setValidation('');
  }

  onAddToCartClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest(ADD_TO_CART_SELECTOR);

    if (
      !(
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
      ) ||
      !this.isProductForm(control.form)
    ) {
      return;
    }

    this.setForm(control.form);

    if (
      !this.isCustom ||
      (!this.transitionPending && !this.editorOpen && this.hasConfirmed())
    ) {
      return;
    }

    this.blockUnconfirmedSubmission(event);
  }

  onSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !this.isProductForm(form)) {
      return;
    }

    this.setForm(form);

    if (!this.isCustom) {
      this.clearProperties();
      return;
    }

    if (
      !this.transitionPending &&
      !this.editorOpen &&
      this.hasConfirmed()
    ) {
      this.syncProperties();
      return;
    }

    this.blockUnconfirmedSubmission(event);
  }

  isProductForm(form) {
    return (
      form instanceof HTMLFormElement &&
      (form === this.productForm || form.id === this.productFormId)
    );
  }

  blockUnconfirmedSubmission(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.openEditor();

    if (!this.measurements.length) {
      this.setValidation(this.dataset.messageMissing || this.dataset.messageRequired);
      return;
    }

    const invalid = this.validateInputs();

    if (invalid) {
      this.setValidation(this.dataset.messageRequired);
      invalid.reportValidity();
      invalid.focus();
      return;
    }

    this.setValidation(this.dataset.messageConfirm);
    this.refs.confirm?.focus();
  }

  setForm(form) {
    if (form === this.productForm) return;

    this.restoreAddToCartState();
    this.productForm = form;
  }

  renderFields() {
    const signature = JSON.stringify(this.measurements);

    if (this.refs.grid.dataset.measurements !== signature) {
      this.captureDraft();

      this.refs.grid.replaceChildren(
        ...this.measurements.map((measurement, index) =>
          this.createField(measurement, index)
        )
      );

      this.refs.grid.dataset.measurements = signature;
      return;
    }

    this.inputs.forEach((input) => {
      const value =
        this.draft.get(input.dataset.measurementName) ??
        this.confirmed.get(input.dataset.measurementName) ??
        '';

      if (input.value !== value) {
        input.value = value;
      }
    });
  }

  createField(measurement, index) {
    const fragment = this.refs.template.content.cloneNode(true);
    const field = fragment.firstElementChild;
    const label = field.querySelector('label');
    const input = field.querySelector('input');
    const inputId = `${this.id}-measurement-${index}`;

    label.htmlFor = inputId;
    label.textContent = `${measurement}:`;

    input.id = inputId;
    input.dataset.measurementName = measurement;
    input.value =
      this.draft.get(measurement) ??
      this.confirmed.get(measurement) ??
      '';

    return field;
  }

  get inputs() {
    return [
      ...this.refs.grid.querySelectorAll('[data-custom-size-measurement]'),
    ];
  }

  captureDraft() {
    this.inputs.forEach((input) => {
      this.draft.set(input.dataset.measurementName, input.value);
    });
  }

  hasConfirmed(measurements = this.measurements) {
    return (
      measurements.length > 0 &&
      measurements.every((name) =>
        String(this.confirmed.get(name) || '').trim()
      )
    );
  }

  confirmMeasurements() {
    if (!this.measurements.length) {
      this.setValidation(this.dataset.messageMissing || this.dataset.messageRequired);
      return;
    }

    const invalid = this.validateInputs();

    if (invalid) {
      this.setValidation(this.dataset.messageRequired);
      invalid.reportValidity();
      invalid.focus();
      return;
    }

    this.inputs.forEach((input) => {
      const value = input.value.trim();

      input.value = value;
      this.draft.set(input.dataset.measurementName, value);
      this.confirmed.set(input.dataset.measurementName, value);
    });

    this.editorOpen = false;
    this.setValidation('');
    this.setEditorVisible(false);
    this.renderView();
  }

  cancelEditing() {
    if (this.hasConfirmed()) {
      this.restoreConfirmed();
      this.editorOpen = false;
      this.setValidation('');
      this.clearInputValidation();
      this.setEditorVisible(false);
      this.renderView();
      return;
    }

    this.measurements.forEach((name) => this.draft.delete(name));
    this.inputs.forEach((input) => {
      input.value = '';
    });

    this.setValidation('');
    this.clearInputValidation();

    const request = new CustomEvent('custom-size:request-standard', {
      bubbles: true,
      cancelable: true,
    });
    const handled = !this.dispatchEvent(request);

    if (!handled) {
      this.editorOpen = true;
      this.setEditorVisible(true);
      this.inputs[0]?.focus();
      this.renderView();
    }
  }

  openEditor(restore = false) {
    if (!this.isCustom) return;
    if (restore) this.restoreConfirmed();

    this.editorOpen = true;
    this.setEditorVisible(true);

    if (!this.measurements.length) {
      this.setValidation(this.dataset.messageMissing || this.dataset.messageRequired);
    }

    this.renderView();
    this.inputs[0]?.focus();
  }

  restoreConfirmed() {
    this.inputs.forEach((input) => {
      const value = String(
        this.confirmed.get(input.dataset.measurementName) || ''
      );

      input.value = value;
      this.draft.set(input.dataset.measurementName, value);
    });
  }

  setEditorVisible(visible) {
    this.inputs.forEach((input) => {
      input.disabled = !visible;
      input.required = visible;
    });

    this.refs.editor.hidden = !visible;
  }

  validateInputs() {
    let firstInvalid = null;

    this.inputs.forEach((input) => {
      const invalid = !input.value.trim();

      input.setCustomValidity(
        invalid ? this.dataset.messageFieldRequired : ''
      );
      input.toggleAttribute('aria-invalid', invalid);

      if (invalid && !firstInvalid) {
        firstInvalid = input;
      }
    });

    return firstInvalid;
  }

  clearInputValidation() {
    this.inputs.forEach((input) => {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
    });
  }

  setValidation(message = '') {
    this.refs.validation.textContent = message || '';
    this.refs.validation.hidden = !message;
  }

  renderView() {
    if (!this.isCustom) {
      this.refs.editor.hidden = true;
    }

    // Hide the custom element itself when the editor is closed so the
    // parent grid does not reserve an empty row or an extra grid gap.
    this.hidden = !this.isCustom || !this.editorOpen;

    this.syncProperties();
    this.updateAddToCartState();
    this.dispatchState();
  }

  dispatchState() {
    this.dispatchEvent(
      new CustomEvent('custom-size:statechange', {
        bubbles: true,
        detail: {
          isCustom: this.isCustom,
          editorOpen: this.editorOpen,
          showConfirmed:
            this.isCustom && this.hasConfirmed() && !this.editorOpen,
          confirmedMeasurements: this.confirmedEntries(),
        },
      })
    );
  }

  setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  confirmedEntries() {
    return this.measurements.map((name) => ({
      name,
      value: String(this.confirmed.get(name) || '').trim(),
    }));
  }

  get addToCartControls() {
    const controls = new Set();

    this.productForm
      ?.querySelectorAll(ADD_TO_CART_SELECTOR)
      .forEach((control) => controls.add(control));

    if (this.productFormId) {
      const formId = escapeCss(this.productFormId);

      document
        .querySelectorAll(
          `[form="${formId}"][name="add"], ` +
            `[form="${formId}"].add-to-cart-button`
        )
        .forEach((control) => controls.add(control));
    }

    return [...controls].filter(
      (control) =>
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
    );
  }

  disableAddToCartControl(control) {
    if (!control.hasAttribute('data-custom-size-disabled')) {
      control.dataset.customSizePreviousDisabled = control.disabled
        ? 'true'
        : 'false';

      const previousAriaDisabled = control.getAttribute('aria-disabled');

      if (previousAriaDisabled !== null) {
        control.dataset.customSizePreviousAriaDisabled =
          previousAriaDisabled;
      }
    }

    control.dataset.customSizeDisabled = '';
    control.disabled = true;
    control.setAttribute('aria-disabled', 'true');
  }

  restoreAddToCartControl(control) {
    if (!control.hasAttribute('data-custom-size-disabled')) return;

    control.disabled =
      control.dataset.customSizePreviousDisabled === 'true';

    if ('customSizePreviousAriaDisabled' in control.dataset) {
      control.setAttribute(
        'aria-disabled',
        control.dataset.customSizePreviousAriaDisabled
      );
    } else {
      control.removeAttribute('aria-disabled');
    }

    delete control.dataset.customSizeDisabled;
    delete control.dataset.customSizePreviousDisabled;
    delete control.dataset.customSizePreviousAriaDisabled;
  }

  updateAddToCartState() {
    const shouldDisable =
      this.transitionPending ||
      (this.isCustom && (this.editorOpen || !this.hasConfirmed()));

    this.addToCartControls.forEach((control) => {
      if (shouldDisable) {
        this.disableAddToCartControl(control);
      } else {
        this.restoreAddToCartControl(control);
      }
    });
  }

  restoreAddToCartState() {
    this.addToCartControls.forEach((control) => {
      this.restoreAddToCartControl(control);
    });
  }

  syncProperties() {
    this.clearProperties();

    if (
      !this.productForm ||
      !this.isCustom ||
      !this.hasConfirmed()
    ) {
      return;
    }

    const inputs = this.measurements.map((name) => {
      const input = document.createElement('input');

      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = String(this.confirmed.get(name) || '').trim();

      if (this.productFormId) {
        input.setAttribute('form', this.productFormId);
      }

      return input;
    });

    this.refs.properties.replaceChildren(...inputs);
  }

  clearProperties() {
    this.refs.properties?.replaceChildren();
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, CustomSizeEditor);
}
